import { Component, ElementRef, input, output, ViewChild } from '@angular/core';
import { uuid } from '@core/helpers';
import { combineLatestWith, Observable, Subject, takeUntil } from 'rxjs';
import { TDropdownParams } from 'src/app/project/components/dropdown/dropdown-service/dropdown-service.consts';
import { DropdownService } from 'src/app/project/components/dropdown/dropdown-service/dropdown.service';
import { TDropdown } from 'src/app/project/components/dropdown/dropdown.consts';
import {
  GroupedMultiSelectDropdownComponent,
  IChildOption,
  IParentOption,
} from 'src/app/project/components/input/grouped-multi-select-dropdown/grouped-multi-select-dropdown.component';
import { EIconPath } from 'src/app/project/shared/enums/icons.enum';
import { AccountService } from '../../../account/account-service/account.service';
import { TAccount } from '../../../account/account.model';
import { TWorkspacesById } from '../../../workspace/workspace.model';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { iconMap } from 'src/app/project/shared/enums/icon-map';

@Component({
  selector: 'pp-filter-field-account-sites',
  templateUrl: './filter-field-account-sites.component.html',
  styleUrls: ['./filter-field-account-sites.component.scss'],
  standalone: false,
})
export class FilterFieldAccountSitesComponent {
  @ViewChild('accountSitesList') accountSitesList: ElementRef;
  ppOnValueChange = output<string[]>();
  ppPreviousSelectedAccounts = input<unknown[]>([]);

  allAccounts: TAccount[];
  formattedAccounts: IParentOption[];
  buttonId = uuid();
  selectedSites: IChildOption[] = [];
  dropdown: TDropdown = this.dropdownService.getDropdown();
  EIconPath = EIconPath;
  iconMap = iconMap;
  workspaces: TWorkspacesById;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private accountService: AccountService,
    private dropdownService: DropdownService,
    private workspaceService: WorkspaceService,
  ) {}

  ngOnChanges(): void {
    this.formatAccounts();
  }

  ngOnInit(): void {
    this.subscribeToAllData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatAccounts(): void {
    this.formattedAccounts =
      this.allAccounts?.map((account) => {
        if (account.workspaces.length > 0) {
          const parentAccount: IParentOption = {
            id: account.accountId,
            label: account.accountName,
            isSelected: false,
            isCollapsed: true,
            isIndeterminate: false,
            isVisible: true,
            children: account.workspaces.map((accountWorkspace) => ({
              id: this.workspaces[accountWorkspace].workspaceId,
              label: this.workspaces[accountWorkspace].siteName,
              isSelected: this.ppPreviousSelectedAccounts()?.includes(
                this.workspaces[accountWorkspace].workspaceId,
              ),
              isVisible: true,
            })),
          };

          const isEveryChildSelected = parentAccount.children.every((child) => child.isSelected);
          parentAccount.isSelected = isEveryChildSelected;
          parentAccount.isIndeterminate =
            !isEveryChildSelected && parentAccount.children.some((child) => child.isSelected);

          parentAccount.isCollapsed = !parentAccount.isIndeterminate;

          return parentAccount;
        }
        return;
      }) ?? [];

    this.selectedSites = this.formattedAccounts.flatMap((parent) =>
      parent.children?.filter((child) => !!child?.isSelected),
    );
  }

  toggleDropdown(): void {
    const modalData = {
      parentOptions: this.formattedAccounts,
      parentTitle: 'accounts',
      placeholderText: 'search_for_accounts_sites',
    };

    const params: TDropdownParams = {
      isModalDropdown: true,
      callback: (options: IChildOption[]) => {
        this.selectedSites = options ?? [];
        const optionIds = options?.map((option) => option.id);
        this.ppOnValueChange.emit(optionIds);
      },
    };

    this.dropdownService.toggleDropdown(
      this.buttonId,
      modalData,
      GroupedMultiSelectDropdownComponent,
      params,
    );
  }
  private subscribeToAllData(): void {
    const workspaces$: Observable<TWorkspacesById> = this.workspaceService.getWorkspacesChange$();

    this.accountService
      .getAccountsChange$()
      .pipe(combineLatestWith(workspaces$), takeUntil(this.destroy$))
      .subscribe(([accounts, workspaces]) => {
        if (accounts?.length > 0 && Object.keys(workspaces).length > 0) {
          this.allAccounts = accounts;
          this.workspaces = workspaces;
          this.formatAccounts();
        }
      });
  }
}
